import { useEffect, useRef, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import UiInputDate from "../../../../../../components/input-date/InputDate.component";
import { Validations } from "../../../../../../services/form/validations.service";
import UiInputTextarea from "../../../../../../components/input-textarea/InputTextarea.component";
import UiSelect from "../../../../../../components/select/Select.component";
import UiSelectMultiple from "../../../../../../components/select-multiple/SelectMultiple.component";
import UiFieldMessage from "../../../../../../components/field-message/FieldMessage.component";
import { FieldMessageSeverity } from "../../../../../../models/components/field-message.model";
import UiButton from "../../../../../../components/button/Button";
import { useNextAppointment } from "../../../../../../hooks/useNextAppointment";
import NoteNextAppointment from "./NoteNextAppointment";
import CustomGoals from "../../custom-goals/CustomGoals";
import { NoteFormData } from "../../../../../../models/notes/note-form";
import { Note, NoteTopics, NoteStateOfChange } from "../../../../../../models/notes/note";
import UiInputRadio from "../../../../../../components/input-radio/InputRadio.component";
import { EndpointsService } from "../../../../../../services/endpoints/endpoints.service";
import { NoteHelper } from "../../../../../../services/helpers/note-helper";
import NoteDraft from "./NoteDraft";
import { useSmartGoals } from "../../../../../../hooks/custom-goals/useSmartGoals";
import { useLongTermGoals } from "../../../../../../hooks/custom-goals/useLongTermGoals";
import { ListOption } from "../../../../../../models/misc.model";
import { ParticipantProfile } from "../../../../../../models/entities/participant.model";
import { FaExclamationTriangle } from "react-icons/fa";
import NoteFormCustomField from "./NoteFormCustomField";
import { useWellnessCheckAdditionalCategories } from "../../../../../../hooks/useWellnessCheckAdditionalCategories";
import {
	WELLNESS_CHECK_OTHER_ID,
	formatWellnessCheckGroupLabel,
	getWellnessCheckTopicId,
	isWellnessType,
	normalizeWellnessCheckGroups,
	wellnessCheckTopicIdToSafeId,
	type WellnessCheckAdditionalCategory,
} from "../../../../../../models/notes/wellness-check-additional-categories.model";
import { Checkbox } from "primereact/checkbox";
import { InputTextarea } from "primereact/inputtextarea";
import { OverlayPanel } from "primereact/overlaypanel";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
function WellnessCheckCategoryList({
	groupName,
	categories,
	groupCount,
	formData,
	setFormData,
	submitting,
}: {
	groupName: string;
	categories: WellnessCheckAdditionalCategory[];
	groupCount: number;
	formData: NoteFormData;
	setFormData: React.Dispatch<React.SetStateAction<NoteFormData>>;
	submitting: boolean;
}) {
	const { t } = useTranslation("common");
	const tooltipOverlayRef = useRef<OverlayPanel>(null);
	const [tooltipContent, setTooltipContent] = useState<string | null>(null);
	const tooltipHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearTooltipHideTimeout = () => {
		if (tooltipHideTimeoutRef.current) {
			clearTimeout(tooltipHideTimeoutRef.current);
			tooltipHideTimeoutRef.current = null;
		}
	};

	const scheduleTooltipHide = () => {
		clearTooltipHideTimeout();
		tooltipHideTimeoutRef.current = setTimeout(() => {
			tooltipOverlayRef.current?.hide();
			setTooltipContent(null);
			tooltipHideTimeoutRef.current = null;
		}, 200);
	};

	const categoriesWithOther: WellnessCheckAdditionalCategory[] = [
		...categories,
		{ name: WELLNESS_CHECK_OTHER_ID, tooltip: "", options: [] },
	];
	return (
		<>
			<OverlayPanel
				ref={tooltipOverlayRef}
				appendTo={typeof document !== "undefined" ? document.body : undefined}
				onHide={() => setTooltipContent(null)}
				className="wellness-check-tooltip-panel"
				dismissable={false}
			>
				{tooltipContent && (
					<div
						className="wellness-check-tooltip-panel__content"
						onMouseEnter={clearTooltipHideTimeout}
						onMouseLeave={() => {
							tooltipOverlayRef.current?.hide();
							setTooltipContent(null);
						}}
					>
						{tooltipContent}
					</div>
				)}
			</OverlayPanel>
			{categoriesWithOther.map((category) => {
				const topicId = getWellnessCheckTopicId(groupName, category.name, groupCount);
				const safeId = wellnessCheckTopicIdToSafeId(topicId);
				const item = formData.wellnessCheckItems?.find((i) => i.topicId === topicId);
				const checked = !!item;
				const currentNotes = item?.notes ?? "";
				const hasOptions = category.options?.length > 0;
				const displayLabel =
					category.name === WELLNESS_CHECK_OTHER_ID
						? t("PARTICIPANT.DASHBOARD.NOTES.WELLNESS_CHECK_OTHER")
						: category.name;
				return (
					<div
						key={topicId}
						className="mb-3"
					>
						<div className="d-flex align-items-center gap-2">
							<Checkbox
								inputId={`wellness-${safeId}`}
								checked={checked}
								onChange={() => {
									const next = checked
										? (formData.wellnessCheckItems ?? []).filter(
												(i) => i.topicId !== topicId
											)
										: [
												...(formData.wellnessCheckItems ?? []),
												{ topicId, notes: null },
											];
									setFormData({ ...formData, wellnessCheckItems: next });
								}}
								disabled={submitting}
							/>
							<label
								htmlFor={`wellness-${safeId}`}
								className="mb-0 cursor-pointer"
							>
								{displayLabel}
							</label>
							{category.tooltip && (
								<span
									role="button"
									tabIndex={0}
									className="wellness-check-info-icon"
									aria-label={t(
										"PARTICIPANT.DASHBOARD.NOTES.WELLNESS_CHECK_TOPICS_INFO"
									)}
									title={t(
										"PARTICIPANT.DASHBOARD.NOTES.WELLNESS_CHECK_TOPICS_INFO"
									)}
									onMouseEnter={(e) => {
										clearTooltipHideTimeout();
										setTooltipContent(category.tooltip);
										tooltipOverlayRef.current?.show(e, e.currentTarget);
									}}
									onMouseLeave={scheduleTooltipHide}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											setTooltipContent(category.tooltip);
											tooltipOverlayRef.current?.show(e, e.currentTarget);
										}
									}}
								>
									<span className="pi pi-info-circle" />
								</span>
							)}
						</div>
						{checked && (
							<div className="ms-4 mt-2">
								<InputTextarea
									id={`wellness-notes-${safeId}`}
									placeholder={t(
										"PARTICIPANT.DASHBOARD.NOTES.WELLNESS_CHECK_TOPICS_NOTES_PLACEHOLDER"
									)}
									onChange={(e) => {
										const next = (formData.wellnessCheckItems ?? []).map((i) =>
											i.topicId === topicId
												? { ...i, notes: e.target.value || null }
												: i
										);
										setFormData({ ...formData, wellnessCheckItems: next });
									}}
									disabled={submitting}
									value={currentNotes}
									rows={2}
									className="w-100"
								/>
								{hasOptions && (
									<div className="mt-2">
										<label className="field-label d-block mb-1 small">
											{t(
												"PARTICIPANT.DASHBOARD.NOTES.WELLNESS_CHECK_ADD_OPTION"
											)}
										</label>
										<Dropdown
											value={null}
											options={category.options.map((opt) => ({
												label: opt,
												value: opt,
											}))}
											onChange={(e: DropdownChangeEvent) => {
												const added = (e.value as string) || "";
												if (!added) return;
												const newNotes = currentNotes
													? `${currentNotes}\n${added}`
													: added;
												const next = (
													formData.wellnessCheckItems ?? []
												).map((i) =>
													i.topicId === topicId
														? { ...i, notes: newNotes }
														: i
												);
												setFormData({
													...formData,
													wellnessCheckItems: next,
												});
											}}
											placeholder={t(
												"PARTICIPANT.DASHBOARD.NOTES.WELLNESS_CHECK_SELECT_OPTION"
											)}
											disabled={submitting}
											className="w-100"
										/>
									</div>
								)}
							</div>
						)}
					</div>
				);
			})}
		</>
	);
}

const NoteForm = ({
	participant,
	note,
	nextAppointmentQuery,
	smartGoalsQuery,
	longTermGoalsQuery,
	onSubmit,
	onCancel,
	onReloadLatest,
}: {
	participant: ParticipantProfile;
	note: Note | null;
	nextAppointmentQuery: ReturnType<typeof useNextAppointment>;
	smartGoalsQuery: ReturnType<typeof useSmartGoals>;
	longTermGoalsQuery: ReturnType<typeof useLongTermGoals>;
	onSubmit: () => void;
	onCancel: () => void;
	onReloadLatest: () => void;
}) => {
	const { t } = useTranslation("common");
	const additionalCategoriesQuery = useWellnessCheckAdditionalCategories();

	const [errorValidating, setErrorValidating] = useState<string | null>(null);
	const [errorGoals, setErrorGoals] = useState<boolean>(false);
	const [errorSubmitting, setErrorSubmitting] = useState<boolean>(false);
	const [conflict, setConflict] = useState<"notFound" | null>(null);

	const [formData, setFormData] = useState<NoteFormData>(
		NoteHelper.setNoteFormData(participant.id, note)
	);

	useEffect(() => {
		if (note) {
			// Editing a note
			NoteHelper.deleteDraft(participant.id);
		}
		setConflict(null);
		setFormData(NoteHelper.setNoteFormData(participant.id, note));
	}, [note]);

	// Normalize API response (object or array of groups) -> sorted array of { groupName, categories }
	const groupEntries = normalizeWellnessCheckGroups(additionalCategoriesQuery.data);

	// When editing a wellness check note, fold items whose category no longer exists into Other (once per note).
	// All content from removed categories is merged into the Other field so no data is lost.
	const foldedNoteIdRef = useRef<number | null>(null);
	useEffect(() => {
		if (!note || !isWellnessType(note.type) || !additionalCategoriesQuery.data) return;
		if (note.id !== foldedNoteIdRef.current) foldedNoteIdRef.current = null;
		if (foldedNoteIdRef.current === note.id) return;
		const entries = normalizeWellnessCheckGroups(additionalCategoriesQuery.data);
		const group = entries.find((e) => e.groupName === note.type);
		if (!group) return;
		const groupCount = 1;
		const validNames = new Set<string>();
		// Current format (single group): plain category name
		group.categories.forEach((c) =>
			validNames.add(getWellnessCheckTopicId(group.groupName, c.name, groupCount))
		);
		validNames.add(
			getWellnessCheckTopicId(group.groupName, WELLNESS_CHECK_OTHER_ID, groupCount)
		);
		// Legacy format: "groupName::categoryName" so old notes still match when category exists
		group.categories.forEach((c) => validNames.add(`${group.groupName}::${c.name}`));
		validNames.add(`${group.groupName}::${WELLNESS_CHECK_OTHER_ID}`);

		const items = formData.wellnessCheckItems ?? [];
		const unmatched = items.filter((i) => !validNames.has(i.topicId));
		if (unmatched.length === 0) return;

		const otherTopicId = getWellnessCheckTopicId(
			group.groupName,
			WELLNESS_CHECK_OTHER_ID,
			groupCount
		);
		// Move all content from removed categories into Other: each line "CategoryName: <full notes>"
		const extraLines: string[] = [];
		unmatched.forEach((u) => {
			const notesContent = (u.notes ?? "").trim();
			const label = u.topicId.includes("::")
				? (u.topicId.split("::")[1] ?? u.topicId)
				: u.topicId;
			const line = notesContent ? `${label}:\n${notesContent}` : `${label}:`;
			extraLines.push(line);
		});
		const kept = items.filter((i) => validNames.has(i.topicId));
		const isOtherTopicId = (topicId: string) =>
			topicId === WELLNESS_CHECK_OTHER_ID || topicId === otherTopicId;
		const newItems = kept.filter((i) => !isOtherTopicId(i.topicId));
		const existingOther = items.find((i) => i.topicId === otherTopicId);
		const extraText = extraLines.join("\n\n");
		const notes = [existingOther?.notes, extraText].filter(Boolean).join("\n\n");
		newItems.push({ topicId: otherTopicId, notes });
		foldedNoteIdRef.current = note.id;
		setFormData((prev) => ({ ...prev, wellnessCheckItems: newItems }));
	}, [note?.id, note?.type, additionalCategoriesQuery.data, formData.wellnessCheckItems]);

	const handleSubmit = async () => {
		setErrorValidating(null);
		setErrorGoals(false);
		setErrorSubmitting(false);
		setConflict(null);

		if (formData.type === "COACHING" && !NoteHelper.isValidCoachNote(formData)) {
			setErrorValidating(t("PARTICIPANT.DASHBOARD.NOTES.COACHING_CALL_MISSING_DATA_ERROR"));
		} else if (formData.type === "COACHING" && !NoteHelper.goalsAreValid(formData)) {
			setErrorGoals(true);
		} else if (formData.type === "OTHER" && !NoteHelper.isValidOtherNote(formData)) {
			setErrorValidating(t("PARTICIPANT.DASHBOARD.NOTES.OTHER_NOTE_MISSING_DATA_ERROR"));
		} else if (
			isWellnessType(formData.type) &&
			!NoteHelper.isValidWellnessCheckNote(formData)
		) {
			setErrorValidating(t("PARTICIPANT.DASHBOARD.NOTES.WELLNESS_CHECK_MISSING_DATA_ERROR"));
		} else {
			if (note) {
				// Update
				await update();
			} else {
				// Create
				await create();
			}
		}
	};

	const create = async () => {
		await EndpointsService.dataRetriever
			.createParticipantNotes({
				body: [
					formData.type === "COACHING"
						? NoteHelper.coachingNote(participant.id, formData)
						: isWellnessType(formData.type)
							? NoteHelper.wellnessCheckNote(participant.id, formData)
							: NoteHelper.otherNote(participant.id, formData),
				],
			})
			.then(() => {
				NoteHelper.deleteDraft(participant.id);
				setFormData(NoteHelper.setNoteFormData(participant.id, null));
				onSubmit();
			})
			.catch(() => setErrorSubmitting(true));
	};

	const update = async () => {
		await EndpointsService.dataRetriever
			.editParticipantNote({
				body:
					formData.type === "COACHING"
						? {
								...NoteHelper.coachingNote(participant.id, formData),
								id: note!.id,
							}
						: isWellnessType(formData.type)
							? {
									...NoteHelper.wellnessCheckNote(participant.id, formData),
									id: note!.id,
								}
							: {
									...NoteHelper.otherNote(participant.id, formData),
									id: note!.id,
								},
			})
			.then(() => {
				setFormData(NoteHelper.setNoteFormData(participant.id, null));
				onSubmit();
			})
			.catch((error: unknown) => {
				if (NoteHelper.isNoteNotFound(error)) setConflict("notFound");
				else setErrorSubmitting(true);
			});
	};

	return (
		<div className="participant_notes_form">
			<div className="participant_notes_form__title">
				{t("PARTICIPANT.DASHBOARD.NOTES.TITLE")}
			</div>

			<Form
				initialValues={formData}
				onSubmit={handleSubmit}
				render={({ handleSubmit, submitting, form }) => (
					<>
						<form onSubmit={handleSubmit}>
							{!note && (
								<NoteDraft
									participantId={participant.id}
									formData={formData}
								/>
							)}

							{/* Date of engagement */}
							<UiInputDate
								id="date"
								name="date"
								placeholder="PARTICIPANT.DASHBOARD.NOTES.DATE_OF_ENGAGEMENT"
								label="PARTICIPANT.DASHBOARD.NOTES.DATE_OF_ENGAGEMENT"
								onChange={(e) =>
									setFormData({
										...formData,
										date: e.target.value as Date | null,
									})
								}
								maxDate={new Date()}
								showTime={true}
								validations={[Validations.required]}
								disabled={submitting}
							/>

							{/* Note type: COACHING, dynamic wellness groups from API, OTHER */}
							<div>
								<UiInputRadio
									id="type"
									name="type"
									display="inline"
									label="PARTICIPANT.DASHBOARD.NOTES.TYPE"
									onChange={(e) => {
										setErrorValidating(null);
										setErrorGoals(false);
										setErrorSubmitting(false);
										setFormData({ ...formData, type: e.target.value });
										form.restart();
									}}
									validations={[Validations.required]}
									disabled={submitting}
									options={[
										{
											id: "COACHING",
											label: "PARTICIPANT.DASHBOARD.NOTES.COACHING_CALL",
										},
										...groupEntries.map((g) => ({
											id: g.groupName,
											label: formatWellnessCheckGroupLabel(g.groupName),
										})),
										{ id: "OTHER", label: "PARTICIPANT.DASHBOARD.NOTES.OTHER" },
									]}
								/>
							</div>

							{formData.type === "COACHING" && (
								<>
									{/* Subjective data */}
									<NoteFormCustomField
										fieldName="subjective"
										fieldLabel="S"
										tooltip="PARTICIPANT.DASHBOARD.NOTES.S"
									>
										<UiInputTextarea
											id="subjective"
											name="subjective"
											placeholder="PARTICIPANT.DASHBOARD.NOTES.SUBJECTIVE_DATA"
											onChange={(e) =>
												setFormData({
													...formData,
													subjective: e.target.value,
												})
											}
											disabled={submitting}
										/>
									</NoteFormCustomField>

									{/* Objective data */}
									<NoteFormCustomField
										fieldName="objective"
										fieldLabel="O"
										tooltip="PARTICIPANT.DASHBOARD.NOTES.O"
									>
										<UiInputTextarea
											id="objective"
											name="objective"
											placeholder="PARTICIPANT.DASHBOARD.NOTES.OBJECTIVE_DATA"
											onChange={(e) =>
												setFormData({
													...formData,
													objective: e.target.value,
												})
											}
											disabled={submitting}
										/>
									</NoteFormCustomField>

									{/* Assessment: State of change + Assessment */}
									<NoteFormCustomField
										fieldName="assessment"
										fieldLabel="A"
										tooltip="PARTICIPANT.DASHBOARD.NOTES.A"
									>
										<>
											{/* Assessment */}
											<UiInputTextarea
												id="assessment"
												name="assessment"
												placeholder="PARTICIPANT.DASHBOARD.NOTES.ASSESSMENT"
												label="PARTICIPANT.DASHBOARD.NOTES.ASSESSMENT"
												onChange={(e) =>
													setFormData({
														...formData,
														assessment: e.target.value,
													})
												}
												disabled={submitting}
											/>
											{/* State of change */}
											<UiSelect
												id="stateOfChange"
												name="stateOfChange"
												placeholder="PARTICIPANT.DASHBOARD.NOTES.STATE_OF_CHANGE"
												label="PARTICIPANT.DASHBOARD.NOTES.STATE_OF_CHANGE"
												className="w-75"
												onChange={(e) =>
													setFormData({
														...formData,
														stateOfChange: e.target.value,
													})
												}
												disabled={submitting}
												showClear
												options={Object.keys(NoteStateOfChange).map(
													(x) =>
														new ListOption({
															id: x,
															label: `PARTICIPANT.DASHBOARD.NOTES.STATE_OF_CHANGE_OPTIONS.${x}`,
														})
												)}
											/>
										</>
									</NoteFormCustomField>

									{/* Plan */}
									<NoteFormCustomField
										fieldName="plan"
										fieldLabel="P"
										tooltip="PARTICIPANT.DASHBOARD.NOTES.P"
									>
										<UiInputTextarea
											id="plan"
											name="plan"
											placeholder="PARTICIPANT.DASHBOARD.NOTES.PLAN"
											onChange={(e) =>
												setFormData({ ...formData, plan: e.target.value })
											}
											disabled={submitting}
										/>
									</NoteFormCustomField>

									<NoteNextAppointment
										nextAppointmentQuery={nextAppointmentQuery}
									/>
								</>
							)}

							{formData.type === "OTHER" && (
								<UiInputTextarea
									id="notes"
									name="notes"
									placeholder="PARTICIPANT.DASHBOARD.NOTES.NOTES"
									label="PARTICIPANT.DASHBOARD.NOTES.NOTES"
									onChange={(e) =>
										setFormData({ ...formData, notes: e.target.value })
									}
									disabled={submitting}
								/>
							)}

							{isWellnessType(formData.type) &&
								(() => {
									const group = groupEntries.find(
										(g) => g.groupName === formData.type
									);
									return group ? (
										<div className="mb-3">
											<label className="field-label d-block mb-2">
												{t(
													"PARTICIPANT.DASHBOARD.NOTES.WELLNESS_CHECK_TOPICS_LABEL"
												)}
											</label>
											{additionalCategoriesQuery.isLoading && (
												<p className="text-muted">
													{t("UI_COMPONENTS.PANEL_LIST.MESSAGES.LOADING")}
												</p>
											)}
											{additionalCategoriesQuery.isError && (
												<p className="text-danger">
													{t("UI_COMPONENTS.FIELD_MESSAGE.HTTP.ERROR")}
												</p>
											)}
											<WellnessCheckCategoryList
												groupName={group.groupName}
												categories={group.categories}
												groupCount={1}
												formData={formData}
												setFormData={setFormData}
												submitting={!!submitting}
											/>
										</div>
									) : additionalCategoriesQuery.isLoading ? (
										<p className="text-muted mb-3">
											{t("UI_COMPONENTS.PANEL_LIST.MESSAGES.LOADING")}
										</p>
									) : null;
								})()}

							{/* Topics (Coaching and Other only) */}
							{(formData.type === "COACHING" || formData.type === "OTHER") && (
								<div>
									<UiSelectMultiple
										id="topics"
										name="topics"
										placeholder="PARTICIPANT.DASHBOARD.NOTES.TOPICS"
										label="PARTICIPANT.DASHBOARD.NOTES.TOPICS_YOU_SPOKE"
										appendTo="self"
										onChange={(e) =>
											setFormData({ ...formData, topics: e.target.value })
										}
										filter={true}
										options={Object.keys(NoteTopics).map(
											(x) =>
												new ListOption({
													id: x,
													label: `PARTICIPANT.DASHBOARD.NOTES.TOPICS_SPOKEN_OPTIONS.${x}`,
												})
										)}
										// maxSelectedLabels={1}
										disabled={submitting}
									/>
								</div>
							)}
						</form>

						<div>
							{formData.type === "COACHING" && (
								<CustomGoals
									noteId={note?.id || null}
									noteProgress={formData.noteProgress}
									smartGoalsQuery={smartGoalsQuery}
									longTermGoalsQuery={longTermGoalsQuery}
									onChange={(e) => setFormData({ ...formData, noteProgress: e })}
								/>
							)}

							{conflict && (
								<div className="form-message">
									<UiFieldMessage
										severity={FieldMessageSeverity.DANGER}
										label="PARTICIPANT.DASHBOARD.NOTES.NOTE_NOT_FOUND_ERROR"
									/>
									<UiButton
										label="PARTICIPANT.DASHBOARD.NOTES.RELOAD_LATEST"
										className="p-button-outlined p-button-rounded mt-2"
										type="button"
										onClick={() => {
											setConflict(null);
											onReloadLatest();
										}}
									/>
								</div>
							)}

							{(errorValidating || errorGoals || errorSubmitting) && (
								<div className="form-message">
									<UiFieldMessage
										severity={FieldMessageSeverity.DANGER}
										label={
											errorValidating
												? errorValidating
												: errorGoals
													? "PARTICIPANT.DASHBOARD.NOTES.ERROR_GOALS"
													: "UI_COMPONENTS.FIELD_MESSAGE.HTTP.ERROR"
										}
									/>
								</div>
							)}

							<div className="action-buttons">
								{note && (
									<UiButton
										id="modalBtnCancel"
										label="UI_COMPONENTS.BUTTONS.CANCEL"
										className="p-button-outlined p-button-rounded"
										type="button"
										onClick={() => {
											NoteHelper.deleteDraft(participant.id);
											setFormData(
												NoteHelper.setNoteFormData(participant.id, null)
											);
											form.reset();
											onCancel();
										}}
										disabled={submitting}
									/>
								)}

								<UiButton
									label={
										submitting
											? "UI_COMPONENTS.BUTTONS.SAVING"
											: note
												? "UI_COMPONENTS.BUTTONS.SAVE_CHANGES"
												: "UI_COMPONENTS.BUTTONS.SAVE"
									}
									className="p-button-rounded"
									loading={submitting}
									disabled={submitting}
									type="submit"
									onClick={handleSubmit}
								/>
							</div>

							{/* Wellness check link */}
							<div className="mt-2 d-flex align-items-center">
								<FaExclamationTriangle
									className="text-danger me-2"
									fontSize={"1.5rem"}
								/>
								<a
									href={participant.redFlagsSurveyLink}
									target="_blank"
									rel="noreferrer noopener"
									className="k-link"
								>
									<span className="k-link-label text-bold">
										{t("PARTICIPANT.DASHBOARD.NOTES.RED_FLAGS_LINK")}
									</span>
								</a>
							</div>

							{/* Ongoing Wellness check link */}
							<div className="mt-2 d-flex align-items-center">
								<FaExclamationTriangle
									className="text-danger me-2"
									fontSize={"1.5rem"}
								/>
								<a
									href={participant.ongoingWellnessCheckTasksSurveyLink}
									target="_blank"
									rel="noreferrer noopener"
									className="k-link"
								>
									<span className="k-link-label text-bold">
										{t(
											"PARTICIPANT.DASHBOARD.NOTES.ONGOING_WELLNESS_CHECK_LINK"
										)}
									</span>
								</a>
							</div>

							{/* EM Encounter link */}
							<div className="mt-2 d-flex align-items-center">
								<FaExclamationTriangle
									className="text-danger me-2"
									fontSize={"1.5rem"}
								/>
								<a
									href={participant.deviceSetupChecklistLink}
									target="_blank"
									rel="noreferrer noopener"
									className="k-link"
								>
									<span className="k-link-label text-bold">
										{t("PARTICIPANT.DASHBOARD.NOTES.DEVICE_SETUP_CHECKLIST")}
									</span>
								</a>
							</div>
						</div>
					</>
				)}
			/>
		</div>
	);
};

export default NoteForm;
